import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, Loader2, ArrowLeft, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import PasswordInput from '../components/PasswordInput';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Le mot de passe doit contenir au moins 8 caractères');
    if (password !== confirm) return setError('Les mots de passe ne correspondent pas');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur, réessayez');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card/80 border border-white/10 rounded-2xl p-8 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h1 className="text-xl font-bold mb-2">Lien invalide</h1>
          <p className="text-sm text-muted-foreground mb-6">Le lien de réinitialisation est incomplet ou expiré.</p>
          <Link to="/forgot-password"><Button variant="outline">Demander un nouveau lien</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-4 h-4" /> Retour à la connexion
          </Link>
        </div>
        <div className="bg-card/80 border border-white/10 rounded-2xl p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center mb-3">
              {done ? <CheckCircle2 className="w-7 h-7 text-primary" /> : <Lock className="w-7 h-7 text-primary" />}
            </div>
            <div className="text-[10px] uppercase tracking-[4px] text-primary mb-1">TRADEX AI</div>
            <h1 className="text-2xl font-bold">{done ? 'Mot de passe réinitialisé' : 'Nouveau mot de passe'}</h1>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              {done ? 'Redirection vers la connexion…' : 'Choisissez un mot de passe fort (min. 8 caractères).'}
            </p>
          </div>

          {!done && (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div className="space-y-2">
                <Label htmlFor="password">Nouveau mot de passe</Label>
                <PasswordInput
                  id="password"
                  data-testid="new-password-input"
                  className="bg-secondary/50 border-white/10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirmer le mot de passe</Label>
                <PasswordInput
                  id="confirm"
                  data-testid="confirm-password-input"
                  className="bg-secondary/50 border-white/10"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
              <Button type="submit" className="w-full py-6 font-mono uppercase tracking-wider" disabled={loading} data-testid="reset-submit">
                {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
                {loading ? 'Enregistrement…' : 'Réinitialiser'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
